import { useState, useEffect, useRef, type FormEvent } from 'react'
import { useParams } from 'react-router-dom'
import { publicApi, type PurchasePayload } from '@/api/public'
import { TicketTypeSelector } from '@/components/public/TicketTypeSelector'
import { PaymentProviderSelector } from '@/components/public/PaymentProviderSelector'
import type { Event, TicketType } from '@/types/event'
import { Spinner } from '@/components/ui/Spinner'

const PHONE_RE = /^(70|75|76|77|78)\d{7}$/

function fmt(n: number) {
  return new Intl.NumberFormat('fr-FR').format(n)
}

function formatDate(d: string) {
  return new Date(d).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })
}

export function EventPage() {
  const { slug } = useParams<{ slug: string }>()

  const [event, setEvent] = useState<Event | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Sélection type
  const [selectedType, setSelectedType] = useState<TicketType | null>(null)
  const [typeError, setTypeError] = useState('')

  // Formulaire achat
  const [quantity, setQuantity] = useState(1)
  const [phone, setPhone] = useState('')
  const [buyerName, setBuyerName] = useState('')
  const [provider, setProvider] = useState<'wave' | 'orange_money'>('wave')
  const [phoneError, setPhoneError] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState('')

  const formRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!slug) return
    publicApi.getEvent(slug)
      .then(setEvent)
      .catch(() => setError('Événement introuvable ou non disponible.'))
      .finally(() => setLoading(false))
  }, [slug])

  const maxQty = selectedType
    ? selectedType.tickets_available !== null
      ? Math.min(10, selectedType.tickets_available)
      : 10
    : 10

  const total = selectedType ? quantity * selectedType.price : 0

  const handleSelectType = (type: TicketType) => {
    setSelectedType(type)
    setTypeError('')
    setQuantity(1)
    setTimeout(() => formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100)
  }

  const handleQty = (delta: number) => {
    setQuantity((q) => Math.max(1, Math.min(maxQty, q + delta)))
  }

  const validatePhone = (v: string) => {
    if (!PHONE_RE.test(v)) {
      setPhoneError('Numéro invalide. Format : 7X XXX XX XX')
      return false
    }
    setPhoneError('')
    return true
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    if (!selectedType) { setTypeError('Veuillez sélectionner une catégorie.'); return }
    if (!validatePhone(phone)) return

    setSubmitting(true)
    setSubmitError('')
    try {
      const payload: PurchasePayload = {
        ticket_type_id: selectedType.id,
        buyer_phone: phone,
        buyer_name: buyerName || undefined,
        quantity,
        payment_provider: provider,
      }
      const result = await publicApi.purchase(slug!, payload)
      // Sauvegarder l'UUID pour redirection après paiement
      sessionStorage.setItem('pending_ticket_uuid', result.ticket_uuid)
      window.location.href = result.payment_url
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message
        ?? 'Une erreur est survenue. Veuillez réessayer.'
      setSubmitError(msg)
    } finally {
      setSubmitting(false)
    }
  }

  // --- Loading ---
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Spinner className="w-6 h-6" />
      </div>
    )
  }

  // --- Erreur ---
  if (error || !event) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="text-center">
          <p className="text-gray-500 text-sm">{error ?? 'Événement introuvable.'}</p>
        </div>
      </div>
    )
  }

  const activeTypes = event.ticket_types.filter((t) => t.is_active)

  return (
    <div className="min-h-screen bg-gray-100 py-0 sm:py-8">
      <div className="max-w-[440px] mx-auto bg-white sm:rounded-2xl sm:shadow-xl overflow-hidden">

        {/* === Hero === */}
        <div className="relative h-52 sm:h-60">
          {event.poster_url ? (
            <img src={event.poster_url} alt={event.name} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-primary-900 via-primary-700 to-primary-600" />
          )}
          {/* Overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />
          {/* Logo */}
          <div className="absolute top-4 left-4">
            <span className="text-white/80 text-xs font-semibold tracking-widest uppercase">TicketFlow</span>
          </div>
          {/* Badge statut */}
          {event.status === 'published' && (
            <div className="absolute top-4 right-4">
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-success-500/90 text-white text-xs font-semibold backdrop-blur-sm">
                <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
                Vente ouverte
              </span>
            </div>
          )}
          {/* Date flottante */}
          <div className="absolute bottom-4 left-4 right-4">
            <p className="text-white/70 text-xs font-medium mb-1 capitalize">{formatDate(event.event_date)}</p>
            <h1 className="text-white text-xl font-bold leading-tight">{event.name}</h1>
          </div>
        </div>

        {/* === Corps === */}
        <div className="px-5 py-5 flex flex-col gap-5">

          {/* Infos */}
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <svg className="w-4 h-4 text-gray-400 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/>
              </svg>
              {event.location}
            </div>
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <svg className="w-4 h-4 text-gray-400 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
              </svg>
              {event.event_time.slice(0, 5)}
            </div>
          </div>

          {/* Programme */}
          {event.matches.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Programme</p>
              <div className="flex flex-col gap-1.5">
                {event.matches.map((m) => (
                  <div key={m.id} className="flex items-center justify-between py-1.5 px-3 rounded-lg bg-gray-50 text-sm">
                    <span className="font-medium text-gray-900">{m.home_team}</span>
                    <span className="text-gray-400 text-xs font-semibold px-2">vs</span>
                    <span className="font-medium text-gray-900">{m.away_team}</span>
                    {m.match_time && <span className="text-gray-400 text-xs font-mono ml-2">{m.match_time.slice(0, 5)}</span>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Description */}
          {event.description && (
            <p className="text-sm text-gray-600 leading-relaxed">{event.description}</p>
          )}

          {/* Sélection type */}
          {activeTypes.length > 0 ? (
            <div>
              <p className="text-sm font-semibold text-gray-900 mb-3">Choisissez votre catégorie</p>
              <TicketTypeSelector
                types={activeTypes}
                selected={selectedType}
                onSelect={handleSelectType}
              />
              {typeError && (
                <p className="text-xs text-danger-600 mt-2 flex items-center gap-1">
                  <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
                  </svg>
                  {typeError}
                </p>
              )}
            </div>
          ) : (
            <div className="text-center py-6 bg-gray-50 rounded-xl">
              <p className="text-sm text-gray-500">Aucune catégorie disponible.</p>
            </div>
          )}

          {/* === Formulaire achat (visible après sélection) === */}
          {selectedType && (
            <div ref={formRef}>
              <div className="h-px bg-gray-100 mb-5" />
              <form onSubmit={handleSubmit} className="flex flex-col gap-4">

                {/* Résumé sélection */}
                <div className="flex items-center justify-between px-4 py-3 bg-primary-50 rounded-xl border border-primary-100">
                  <div>
                    <p className="text-sm font-semibold text-primary-800">{selectedType.name}</p>
                    {selectedType.description && (
                      <p className="text-xs text-primary-600 mt-0.5">{selectedType.description}</p>
                    )}
                  </div>
                  <p className="text-sm font-bold font-mono text-primary-700">{fmt(selectedType.price)} FCFA</p>
                </div>

                {/* Quantité */}
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-2 block">Nombre de places</label>
                  <div className="flex items-center gap-4">
                    <button
                      type="button"
                      onClick={() => handleQty(-1)}
                      disabled={quantity <= 1}
                      className="w-10 h-10 rounded-full border-2 border-gray-200 flex items-center justify-center text-gray-700 hover:border-gray-300 disabled:opacity-30 transition-colors"
                    >
                      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <line x1="5" y1="12" x2="19" y2="12"/>
                      </svg>
                    </button>
                    <span className="text-2xl font-bold text-gray-900 font-mono w-8 text-center">{quantity}</span>
                    <button
                      type="button"
                      onClick={() => handleQty(1)}
                      disabled={quantity >= maxQty}
                      className="w-10 h-10 rounded-full border-2 border-gray-200 flex items-center justify-center text-gray-700 hover:border-gray-300 disabled:opacity-30 transition-colors"
                    >
                      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
                      </svg>
                    </button>
                    <span className="text-sm text-gray-400 ml-1">
                      {quantity} × {fmt(selectedType.price)} FCFA
                    </span>
                  </div>
                </div>

                {/* Téléphone */}
                <div className="flex flex-col gap-1.5">
                  <label className="text-sm font-medium text-gray-700">
                    Numéro WhatsApp <span className="text-danger-500">*</span>
                  </label>
                  <div className="relative">
                    <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-sm text-gray-500 font-medium">+221</span>
                    <input
                      type="tel"
                      value={phone}
                      onChange={(e) => { setPhone(e.target.value); if (phoneError) validatePhone(e.target.value) }}
                      onBlur={() => validatePhone(phone)}
                      placeholder="77 123 45 67"
                      className={`w-full pl-14 pr-4 py-2.5 rounded-lg border text-base text-gray-900 placeholder:text-gray-400 bg-white transition-all focus:outline-none focus:ring-1 ${
                        phoneError ? 'border-danger-500 focus:border-danger-500 focus:ring-danger-500' : 'border-gray-200 focus:border-primary-500 focus:ring-primary-500'
                      }`}
                    />
                  </div>
                  {phoneError && (
                    <p className="text-xs text-danger-600 flex items-center gap-1">
                      <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
                      </svg>
                      {phoneError}
                    </p>
                  )}
                  <p className="text-xs text-gray-400">Votre ticket sera envoyé sur ce numéro.</p>
                </div>

                {/* Prénom */}
                <div className="flex flex-col gap-1.5">
                  <label className="text-sm font-medium text-gray-700">Prénom <span className="text-gray-400 font-normal">(optionnel)</span></label>
                  <input
                    type="text"
                    value={buyerName}
                    onChange={(e) => setBuyerName(e.target.value)}
                    placeholder="Fatou"
                    className="w-full px-3.5 py-2.5 rounded-lg border border-gray-200 text-base text-gray-900 placeholder:text-gray-400 bg-white focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500 transition-all"
                  />
                </div>

                {/* Provider */}
                <div className="flex flex-col gap-1.5">
                  <label className="text-sm font-medium text-gray-700">Mode de paiement</label>
                  <PaymentProviderSelector value={provider} onChange={setProvider} />
                </div>

                {/* Erreur submit */}
                {submitError && (
                  <div className="flex items-start gap-2.5 p-3 rounded-lg bg-danger-50 border border-danger-200">
                    <svg className="w-4 h-4 text-danger-600 mt-0.5 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
                    </svg>
                    <p className="text-sm text-danger-700">{submitError}</p>
                  </div>
                )}

                {/* Bouton paiement */}
                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full py-4 rounded-xl bg-primary-600 hover:bg-primary-700 text-white font-bold text-base shadow-lg shadow-primary-600/25 transition-all duration-150 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
                >
                  {submitting ? (
                    <span className="flex items-center justify-center gap-2">
                      <Spinner className="w-5 h-5 text-white" />
                      Traitement en cours...
                    </span>
                  ) : (
                    `Payer maintenant — ${fmt(total)} FCFA`
                  )}
                </button>

                <p className="text-center text-xs text-gray-400 pb-2">
                  Paiement sécurisé · {quantity} place{quantity > 1 ? 's' : ''} · {selectedType.name}
                </p>

              </form>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
